import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import TargetSelector from 'components/TargetSelector';
import { ModernStatCard, ModernCard, ModernBadge, ModernButton } from '../components/ModernUI';
import {
  Shield,
  BarChart2,
  CheckCircle2,
  AlertTriangle,
  Activity,
  Clock,
  RefreshCw,
  ChevronRight,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

const DashboardOverview = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState({
    id: 5,
    name: 'www.gsa.gov',
    displayName: 'GSA - General Services Administration'
  });

  const loadDashboardData = useCallback(async (targetId) => {
    if (!targetId) return;

    try {
      setLoading(true);
      setError(null);

      console.log(`[Dashboard] Loading data for target ${targetId}`);

      const response = await fetch(`http://localhost:4000/api/dashboard/overview?target=${targetId}`);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API request failed');
      }

      console.log('[Dashboard] Data loaded successfully:', result.data);
      setDashboardData(result.data);

    } catch (err) {
      console.error('[Dashboard] Error loading data:', err);
      setError(err.message);
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTarget?.id) {
      loadDashboardData(selectedTarget.id);
    }
  }, [selectedTarget, loadDashboardData]);

  const handleTargetChange = useCallback((target) => {
    if (!target || target.id === selectedTarget?.id) return;
    console.log('[Dashboard] Target changed to:', target);
    setSelectedTarget(target);
  }, [selectedTarget]);

  const getStatusVariant = (passRate) => {
    if (passRate >= 90) return 'success';
    if (passRate >= 70) return 'warning';
    return 'danger';
  };

  const getStatusIcon = (passRate) => {
    if (passRate >= 90) return CheckCircle2;
    if (passRate >= 70) return AlertTriangle;
    return AlertCircle;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Loading compliance data...
              </h3>
              <p className="text-gray-600">
                Analyzing {selectedTarget?.displayName || 'selected target'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6">
        <div className="max-w-7xl mx-auto">
          <ModernCard>
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <ModernButton
                variant="primary"
                icon={RefreshCw}
                onClick={() => selectedTarget && loadDashboardData(selectedTarget.id)}
              >
                Retry
              </ModernButton>
            </div>
          </ModernCard>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6">
        <div className="max-w-7xl mx-auto">
          <ModernCard>
            <div className="text-center py-8">
              <BarChart2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Data Available</h3>
              <p className="text-gray-600">No compliance data found for this target</p>
            </div>
          </ModernCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Target Selector */}
        <div className="mb-6">
          <TargetSelector
            selectedTarget={selectedTarget}
            onTargetChange={handleTargetChange}
          />
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Federal Compliance Dashboard</h1>
                <p className="text-gray-600 mt-1">
                  <strong className="text-blue-600">{selectedTarget?.displayName || 'Unknown Target'}</strong> Compliance Assessment
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Click on any framework to view detailed controls and remediation plans
                </p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-right">
              <div className="text-sm font-medium text-gray-500 mb-1">Last Scan</div>
              <div className="text-lg font-semibold text-gray-900">{dashboardData.lastScan}</div>
              <div className="text-xs text-gray-500 mt-1">
                <Clock className="w-3 h-3 inline mr-1" />
                {dashboardData.totalScanners} scanners • {dashboardData.scanDuration}
              </div>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <ModernStatCard
            title="Total Frameworks"
            value={dashboardData.metrics.totalFrameworks}
            icon={Shield}
            iconBgColor="bg-blue-500"
          />
          <ModernStatCard
            title="Controls Scanned"
            value={dashboardData.metrics.controlsScanned.toLocaleString()}
            icon={BarChart2}
            iconBgColor="bg-purple-500"
          />
          <ModernStatCard
            title="Avg Compliance"
            value={`${dashboardData.metrics.avgCompliance}%`}
            icon={getStatusIcon(dashboardData.metrics.avgCompliance)}
            iconBgColor={
              dashboardData.metrics.avgCompliance >= 90 ? 'bg-green-500' :
              dashboardData.metrics.avgCompliance >= 70 ? 'bg-amber-500' :
              'bg-red-500'
            }
            trend={dashboardData.metrics.avgCompliance >= 70 ? 'up' : 'down'}
          />
          <ModernStatCard
            title="Critical Issues"
            value={dashboardData.metrics.criticalIssues}
            icon={AlertTriangle}
            iconBgColor="bg-red-500"
          />
        </div>

        {/* Frameworks Section */}
        <div className="mb-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Compliance Frameworks</h2>
            <p className="text-gray-600">Real-time compliance status for {selectedTarget?.displayName}</p>
          </div>

          <div className="space-y-6">
            {dashboardData.frameworks.map((framework) => {
              const StatusIcon = getStatusIcon(framework.passRate);

              return (
                <div
                  key={framework.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    {/* Framework Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className={`w-12 h-12 rounded-lg ${
                          framework.passRate >= 90 ? 'bg-green-100' :
                          framework.passRate >= 70 ? 'bg-amber-100' :
                          'bg-red-100'
                        } flex items-center justify-center flex-shrink-0`}>
                          <StatusIcon className={`w-6 h-6 ${
                            framework.passRate >= 90 ? 'text-green-600' :
                            framework.passRate >= 70 ? 'text-amber-600' :
                            'text-red-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{framework.name}</h3>
                            <ModernBadge variant={getStatusVariant(framework.passRate)} size="md">
                              {framework.passRate}%
                            </ModernBadge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{framework.description}</p>
                          <ModernBadge variant="default" size="sm">
                            {framework.category}
                          </ModernBadge>
                        </div>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center">
                        <div className={`text-2xl font-bold ${
                          framework.passRate >= 90 ? 'text-green-600' :
                          framework.passRate >= 70 ? 'text-amber-600' :
                          'text-red-600'
                        }`}>
                          {framework.passRate}%
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Pass Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{framework.controlsScanned}</div>
                        <div className="text-xs text-gray-500 mt-1">Controls</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{framework.totalViolations || 0}</div>
                        <div className="text-xs text-gray-500 mt-1">Violations</div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
                        <div
                          className="bg-green-500"
                          style={{
                            width: `${(framework.compliant / (framework.compliant + framework.nonCompliant)) * 100}%`
                          }}
                          title={`${framework.compliant} Compliant Controls`}
                        />
                        <div
                          className="bg-red-500"
                          style={{
                            width: `${(framework.nonCompliant / (framework.compliant + framework.nonCompliant)) * 100}%`
                          }}
                          title={`${framework.nonCompliant} Non-Compliant Controls`}
                        />
                      </div>
                    </div>

                    {/* Controls Summary */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-6">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-green-500" />
                          <span className="text-sm font-medium text-gray-700">{framework.compliant}</span>
                          <span className="text-sm text-gray-500">Compliant</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-red-500" />
                          <span className="text-sm font-medium text-gray-700">{framework.nonCompliant}</span>
                          <span className="text-sm text-gray-500">Non-Compliant</span>
                        </div>
                        {framework.critical > 0 && (
                          <div className="flex items-center space-x-2">
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                            <span className="text-sm font-medium text-red-600">{framework.critical}</span>
                            <span className="text-sm text-red-600">Critical</span>
                          </div>
                        )}
                      </div>
                      <Link
                        to={`/framework/${framework.id}?target=${selectedTarget.id}`}
                        className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
                      >
                        <span>View Details & Remediation</span>
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Info */}
        <ModernCard>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">About This Assessment</h3>
            <p className="text-gray-600 mb-4">
              This compliance dashboard shows real-time analysis results for{' '}
              <strong className="text-blue-600">{selectedTarget?.displayName}</strong> across{' '}
              {dashboardData.metrics.totalFrameworks} federal compliance frameworks.
              Data is generated from comprehensive security scans including accessibility, security, and design standards testing.
            </p>
            <div className="flex items-center justify-center space-x-8 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <BarChart2 className="w-4 h-4" />
                <span>{dashboardData.metrics.controlsScanned.toLocaleString()} controls assessed</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>Last updated: {dashboardData.lastScan}</span>
              </div>
              <div className="flex items-center space-x-2">
                <RefreshCw className="w-4 h-4" />
                <span>Automated scanning every 24 hours</span>
              </div>
            </div>
          </div>
        </ModernCard>
      </div>
    </div>
  );
};

export default DashboardOverview;
